import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  documentSignatureService,
  type DocumentSignatureDto,
  type SignaturePlacementInputDto,
} from '../../../services/documentSignatureService';
import { HtmlSignaturePlacementEditor } from '../../Signature/HtmlSignaturePlacementEditor';
import { PdfSignaturePlacementEditor } from '../../Signature/PdfSignaturePlacementEditor';
import { useToast } from '../../UI/ToastProvider';

export interface SignaturePlacementModalProps {
  open: boolean;
  documentId: string;
  documentVersionId: string | null;
  nodeType: 'pdf' | 'edoc';
  signatureRequestId: string;
  onClose: () => void;
  onConfirm: (placements: Record<string, SignaturePlacementInputDto | null>) => void | Promise<void>;
}

interface SignerPlacementState {
  signerId: string;
  name: string;
  email: string;
  placement: SignaturePlacementInputDto | null;
}

export const SignaturePlacementModal: React.FC<SignaturePlacementModalProps> = ({
  open,
  documentId,
  documentVersionId,
  nodeType,
  signatureRequestId,
  onClose,
  onConfirm,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signatureRequest, setSignatureRequest] = useState<DocumentSignatureDto | null>(null);
  const [signersPlacements, setSignersPlacements] = useState<SignerPlacementState[]>([]);
  const [activeSignerId, setActiveSignerId] = useState<string | null>(null);

  useEffect(() => {
    if (!open)
    {
      return;
    }

    const loadSignatureRequest = async () => {
      setLoading(true);
      try {
        const request = await documentSignatureService.getSignatureRequest(signatureRequestId);
        setSignatureRequest(request);
        
        const placements: SignerPlacementState[] = request.signers.map((signer) => ({
          signerId: signer.id,
          name: signer.name,
          email: signer.email,
          placement: signer.placementPage
            ? {
                page: signer.placementPage,
                x: signer.placementX ?? 0,
                y: signer.placementY ?? 0,
                width: signer.placementWidth ?? undefined,
                height: signer.placementHeight ?? undefined,
              }
            : null,
        }));
        
        setSignersPlacements(placements);
        
        if (placements.length > 0)
        {
          setActiveSignerId(placements[0].signerId);
        }
      } catch (error) {
        addToast(
          error instanceof Error ? error.message : 'Não foi possível carregar a solicitação de assinatura.',
          'error'
        );
        onClose();
      } finally {
        setLoading(false);
      }
    };

    void loadSignatureRequest();
  }, [open, signatureRequestId, addToast, onClose]);

  const handlePlacementChange = (signerId: string, placement: SignaturePlacementInputDto | null) => {
    setSignersPlacements((prev) =>
      prev.map((s) => (s.signerId === signerId ? { ...s, placement } : s))
    );
  };

  const handleUseDefaultPositions = async () => {
    setSubmitting(true);
    try {
      await onConfirm({});
    } catch (error) {
      addToast(
        error instanceof Error ? error.message : 'Não foi possível confirmar as posições padrão.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    const placementsMap: Record<string, SignaturePlacementInputDto | null> = {};
    
    signersPlacements.forEach((signer) => {
      placementsMap[signer.signerId] = signer.placement;
    });

    setSubmitting(true);
    try {
      await onConfirm(placementsMap);
    } catch (error) {
      addToast(
        error instanceof Error ? error.message : 'Não foi possível confirmar os posicionamentos.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const activeSigner = signersPlacements.find((s) => s.signerId === activeSignerId);

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !submitting && !loading && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl rounded-lg bg-white shadow-xl">
                <div className="border-b px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Posicionar Assinaturas
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-500">
                    Defina onde as assinaturas aparecerão no documento ou use as posições padrão.
                  </p>
                </div>

                <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 py-4">
                  {loading && (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-500">Carregando signatários...</p>
                    </div>
                  )}

                  {!loading && signatureRequest && signersPlacements.length > 0 && (
                    <>
                      <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                        <strong>Posicionamento opcional.</strong> Você pode posicionar cada assinatura individualmente 
                        ou usar as posições padrão (rodapé da última página).
                      </div>

                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Signatário para posicionar
                        </label>
                        <select
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          value={activeSignerId ?? ''}
                          onChange={(e) => setActiveSignerId(e.target.value || null)}
                          disabled={submitting}
                        >
                          {signersPlacements.map((s) => (
                            <option key={s.signerId} value={s.signerId}>
                              {s.name} ({s.email})
                            </option>
                          ))}
                        </select>

                        {activeSigner && nodeType === 'pdf' && (
                          <PdfSignaturePlacementEditor
                            documentId={documentId}
                            signerName={activeSigner.name}
                            placement={activeSigner.placement}
                            onPlacementChange={(placement) =>
                              handlePlacementChange(activeSigner.signerId, placement)
                            }
                          />
                        )}

                        {activeSigner && nodeType === 'edoc' && (
                          <HtmlSignaturePlacementEditor
                            documentId={documentId}
                            signerName={activeSigner.name}
                            placement={activeSigner.placement}
                            onPlacementChange={(placement) =>
                              handlePlacementChange(activeSigner.signerId, placement)
                            }
                          />
                        )}
                      </div>
                    </>
                  )}

                  {!loading && signatureRequest && signersPlacements.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-500">Nenhum signatário encontrado.</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 border-t px-6 py-4">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting || loading}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleUseDefaultPositions()}
                    disabled={submitting || loading}
                    className="rounded-md border border-indigo-300 px-4 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                  >
                    Usar Posições Padrão
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleConfirm()}
                    disabled={submitting || loading}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {submitting ? 'Confirmando...' : 'Confirmar'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
